/**
 * Canonical list of portal pages that can be linked from a roadmap task.
 *
 * Department sub-pages are derived from the department tab configs (the source
 * of truth for the portal hubs) so this list stays in sync as tabs change.
 * Everything else mirrors the `/portal` routes in App.js and the sidebar.
 *
 * Values are stored with a leading slash (`/portal/...`). Older roadmap tasks
 * were saved without one, and some pointed at pages that have since been
 * retired or folded into a hub tab — `resolvePortalPagePath` normalizes both.
 */
import { SALES_TABS, getSalesTabHref } from '../utils/salesTabs';
import { OPERATIONS_TABS, getOperationsTabHref } from '../utils/operationsTabs';
import { TECHNOLOGY_TABS, getTechnologyTabHref } from '../utils/technologyTabs';
import { FINANCE_TABS, getFinanceTabHref } from '../utils/financeTabs';
import { MARKETING_TABS, getMarketingTabHref } from '../utils/marketingTabs';
import { HR_TABS, getHRTabHref } from '../utils/hrTabs';

/** Tab ids that are pure redirects to another page — not worth listing separately. */
const TECHNOLOGY_EXCLUDED_TABS = new Set([
  'cost-groups',
  'schedules',
  'dashboards',
  'todos',
  'estimating-template',
]);

const tabOptions = (tabs, hrefFn, exclude = new Set()) =>
  tabs
    .filter((tab) => !exclude.has(tab.id))
    .map((tab) => ({ value: hrefFn(tab), label: tab.label, emoji: tab.emoji }));

/** Grouped page options, in the order they appear in the picker. */
export const PORTAL_PAGE_GROUPS = [
  {
    group: 'General',
    options: [
      { value: '/portal', label: 'Dashboard', emoji: '🏠' },
      { value: '/portal/roadmap', label: 'Roadmap', emoji: '🗺️' },
      { value: '/portal/courses', label: 'Courses', emoji: '🎓' },
      { value: '/portal/tasks', label: 'Tasks', emoji: '✅' },
      { value: '/portal/portal-calendar', label: 'Calendar', emoji: '📅' },
      { value: '/portal/community', label: 'Community', emoji: '💬' },
      { value: '/portal/messages', label: 'Messages', emoji: '✉️' },
      { value: '/portal/conference', label: 'Conference', emoji: '🎥' },
      { value: '/portal/summit', label: 'BBA Summit', emoji: '🏔️' },
      { value: '/portal/collection', label: 'Collection', emoji: '🗂️' },
      { value: '/portal/my-questions', label: 'My Questions', emoji: '❓' },
      { value: '/portal/goals', label: 'Goals', emoji: '🎯' },
      { value: '/portal/bookonsite', label: 'Book On-Site Visit', emoji: '📍' },
      { value: '/portal/bookcqc', label: 'Book CQC Visit', emoji: '🔎' },
      { value: '/portal/kickoff', label: 'Kickoff', emoji: '🚀' },
      { value: '/portal/profile', label: 'My Profile', emoji: '👤' },
      { value: '/portal/billing', label: 'Billing', emoji: '💳' },
      { value: '/portal/proposal', label: 'Proposal Builder', emoji: '📝' },
      { value: '/portal/takeoffs', label: 'Takeoffs', emoji: '📐' },
    ],
  },
  { group: 'Sales', options: tabOptions(SALES_TABS, getSalesTabHref) },
  {
    group: 'Operations',
    options: [
      ...tabOptions(OPERATIONS_TABS, getOperationsTabHref),
      { value: '/portal/jobtread', label: 'JobTread', emoji: '🏗️' },
      { value: '/portal/proposed-org-chart', label: 'Proposed Org Chart', emoji: '🏢' },
    ],
  },
  {
    group: 'Technology',
    options: tabOptions(TECHNOLOGY_TABS, getTechnologyTabHref, TECHNOLOGY_EXCLUDED_TABS),
  },
  { group: 'Finance', options: tabOptions(FINANCE_TABS, getFinanceTabHref) },
  { group: 'Marketing', options: tabOptions(MARKETING_TABS, getMarketingTabHref) },
  {
    group: 'HR',
    options: [
      ...tabOptions(HR_TABS, getHRTabHref),
      { value: '/portal/hr/goals', label: 'Goals', emoji: '🎯' },
      { value: '/portal/hr/onboarding', label: 'Onboarding', emoji: '🪪' },
      { value: '/portal/hr/company-policy', label: 'Company Policy', emoji: '📜' },
      { value: '/portal/hr/job-listings', label: 'Job Listings', emoji: '📢' },
      { value: '/portal/hr/interview-evaluation', label: 'Interview Evaluation', emoji: '🗒️' },
    ],
  },
];

/** Flat option list, each carrying its group name. */
export const PORTAL_PAGE_OPTIONS = PORTAL_PAGE_GROUPS.flatMap(({ group, options }) =>
  options.map((option) => ({ ...option, group }))
);

/**
 * Pages that no longer exist (or moved into a hub tab) mapped to their current
 * home, so roadmap tasks saved against the old portal still land somewhere real.
 * Keys are normalized (leading slash, lowercased) paths.
 */
const LEGACY_PORTAL_PAGE_MAP = {
  '/portal/': '/portal',
  '/portal/calendar': '/portal/portal-calendar',
  '/portal/whats-new': '/portal',
  '/portal/inventory': '/portal',
  '/portal/website-analytics': '/portal/marketing/analytics',
  '/portal/programs': '/portal/billing',
  '/portal/alacarte': '/portal/billing',
  // Folded into the Operations hub
  '/portal/sops': '/portal/operations/sops',
  '/portal/kpis': '/portal/operations/kpis',
  '/portal/job-descriptions': '/portal/operations/job-descriptions',
  '/portal/org-chart': '/portal/operations/org-chart',
  // Folded into the HR hub
  '/portal/my-mission-vision-values': '/portal/hr/company-direction',
  '/portal/hr/my-mission-vision-values': '/portal/hr/company-direction',
  '/portal/hr/employee-materials': '/portal/hr/materials',
  // Retired sales pages
  '/portal/sales/close-ratio': '/portal/sales/analytics',
  '/portal/sales/project-runway': '/portal/sales/analytics',
  '/portal/sales/sales-scripts': '/portal/sales',
  '/portal/sales/pardon-our-dust': '/portal/sales',
  '/portal/sales/referral-programs': '/portal/sales',
  '/portal/sales-referral-programs': '/portal/sales',
  '/portal/sales-pardon-our-dust': '/portal/sales',
  '/portal/sales-scripts': '/portal/sales',
  '/portal/sales-proposals': '/portal/sales/proposals',
  // Retired finance pages
  '/portal/finance/forecast': '/portal/finance/all',
  '/portal/finance/health': '/portal/finance/all',
  '/portal/finance/metrics': '/portal/finance/all',
  // Retired marketing pages
  '/portal/marketing/leadsmarketing': '/portal/marketing/analytics',
  '/portal/marketing/leads-roi': '/portal/marketing/lead-roi',
  '/portal/marketing/social-media': '/portal/marketing/analytics',
  '/portal/marketing/paid-media': '/portal/marketing/analytics',
  '/portal/marketing/newsletter': '/portal/marketing/analytics',
  // Retired technology pages
  '/portal/technology/calculators': '/portal/marketing/calculators',
  '/portal/technology/estimating-template': '/portal/technology/jobtread-imports',
  '/portal/technology/cost-groups': '/portal/technology/jobtread-imports',
  '/portal/technology/schedules': '/portal/technology/jobtread-imports',
  '/portal/technology/dashboards': '/portal/technology/jobtread-imports',
  '/portal/technology/todos': '/portal/technology/jobtread-imports',
};

const normalize = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  // Drop a trailing slash, but keep a bare '/portal'
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : withSlash;
};

/**
 * Turn a stored `portalPage` value into a current, navigable path.
 * @returns {string|null} e.g. '/portal/operations/sops', or null when empty.
 */
export function resolvePortalPagePath(value) {
  const path = normalize(value);
  if (!path) return null;
  const [base, rest] = [path.split('?')[0], path.includes('?') ? path.slice(path.indexOf('?')) : ''];
  const mapped = LEGACY_PORTAL_PAGE_MAP[base.toLowerCase()] || LEGACY_PORTAL_PAGE_MAP[base];
  return `${mapped || base}${rest}`;
}

/** Find the option matching a stored value, following legacy redirects. */
export function findPortalPageOption(value) {
  const resolved = resolvePortalPagePath(value);
  if (!resolved) return null;
  return (
    PORTAL_PAGE_OPTIONS.find((option) => option.value === resolved) ||
    PORTAL_PAGE_OPTIONS.find((option) => option.value.toLowerCase() === resolved.toLowerCase()) ||
    null
  );
}

/** Human label for a stored value — 'Operations · SOPs' — falling back to the path. */
export function getPortalPageLabel(value) {
  const option = findPortalPageOption(value);
  if (option) {
    return option.group === 'General' ? option.label : `${option.group} · ${option.label}`;
  }
  return resolvePortalPagePath(value) || '';
}
